import {
  AlignBottomIcon,
  AlignCenterVerticalIcon,
  AlignLeftIcon,
  AlignRightIcon,
  AlignTopIcon,
  CenterAlignHorizontalIcon,
} from '@persona-id/design-tokens/icons';
import { useCallback, useMemo } from 'react';
import { batch } from 'react-redux';

import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import {
  StyleInput,
  ToggleStyleInput,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs';
import {
  SectionColumn,
  SectionRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/shared';
import {
  StyleValueAlignment,
  StyleValueAxis,
  StyleValueFramePositioning,
  StyleValueJustify,
  StyleValueSizeConfig,
} from 'lib/inquiryStyles';
import {
  ParentLayoutComponent,
  ParentLayoutComponentConfig,
  ParentLayoutKeys,
} from 'lib/interfaces/ui-components';

type ParentLayoutStyleAttributes = {
  alignment: {
    label: string;
    default: StyleValueAlignment;
  };
  justify: {
    label: string;
    default: StyleValueJustify;
  };
  axis: {
    default: StyleValueAxis;
  };
  gap: {
    label: string;
    default: StyleValueSizeConfig;
  };
  'frame-positioning': {
    label: string;
    default: StyleValueFramePositioning;
  };
};

const DEFAULT_PARENT_LAYOUT_ATTRIBUTES: ParentLayoutStyleAttributes = {
  alignment: {
    label: 'Alignment',
    default: StyleValueAlignment.Center,
  },
  justify: {
    label: 'Alignment',
    default: StyleValueJustify.Center,
  },
  axis: {
    default: StyleValueAxis.Vertical,
  },
  gap: {
    label: 'Spacing',
    default: {
      value: 0,
      unit: 'px',
    },
  },
  'frame-positioning': {
    label: 'Frame positioning',
    default: StyleValueFramePositioning.InsideMargins,
  },
};

/**
 * Normalizes stack child sizes based on axis:
 * 0 - intrinsic sizing
 * 1 - width is determined by how many elements are in the container
 * @see front-end/inquiry/components/StackBase/StackBase.tsx:52
 */
const getChildSizesArray = (component: ParentLayoutComponentConfig, direction: StyleValueAxis) => {
  const deriveChildFlex = (direction: StyleValueAxis) =>
    direction === StyleValueAxis.Horizontal ? 1 : 0;

  const containerChildren = component.config.children;
  if (containerChildren.length === 0) {
    return [deriveChildFlex(direction)];
  }

  return containerChildren.map(() => deriveChildFlex(direction));
};

const PARENT_LAYOUT_OPTIONS = {
  alignment: {
    [StyleValueAxis.Horizontal]: [
      { label: <AlignTopIcon size={12} />, value: StyleValueAlignment.Start },
      {
        label: <AlignCenterVerticalIcon size={12} />,
        value: StyleValueAlignment.Center,
      },
      { label: <AlignBottomIcon size={12} />, value: StyleValueAlignment.End },
    ],
    [StyleValueAxis.Vertical]: [
      { label: <AlignLeftIcon size={12} />, value: StyleValueAlignment.Start },
      {
        label: <CenterAlignHorizontalIcon size={12} />,
        value: StyleValueAlignment.Center,
      },
      { label: <AlignRightIcon size={12} />, value: StyleValueAlignment.End },
    ],
  },
  justify: [
    { label: <AlignLeftIcon size={12} />, value: StyleValueJustify.Start },
    {
      label: <CenterAlignHorizontalIcon size={12} />,
      value: StyleValueJustify.Center,
    },
    { label: <AlignRightIcon size={12} />, value: StyleValueJustify.End },
  ],
  axis: [
    { label: 'Vertical', value: StyleValueAxis.Vertical },
    { label: 'Horizontal', value: StyleValueAxis.Horizontal },
  ],
  'frame-positioning': [
    { label: 'Inside margins', value: StyleValueFramePositioning.InsideMargins },
    { label: 'Edge-to-edge', value: StyleValueFramePositioning.EdgeToEdge },
  ],
};

type ParentLayoutStylesProps = {
  componentPreview: ParentLayoutComponent;
  component: ParentLayoutComponentConfig;
  styleVariantIndex: number;
  editable: boolean;
  submitStyle: (field: string, values: any) => void;
};

const ParentLayoutStyles = ({
  componentPreview,
  component,
  styleVariantIndex,
  editable,
  submitStyle,
}: ParentLayoutStylesProps) => {
  const parentLayoutDefaults: ParentLayoutStyleAttributes = useMemo(() => {
    const parentLayoutMapTuples = Object.entries(DEFAULT_PARENT_LAYOUT_ATTRIBUTES).map(
      (styleAssignment) => {
        const [style, props] = styleAssignment;
        const defaultValue =
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          componentPreview.styles?.[style as ParentLayoutKeys]?.base?.base ?? props.default;
        return [style, { ...props, default: defaultValue }];
      },
    );

    return Object.fromEntries(parentLayoutMapTuples);
  }, [componentPreview.styles]);

  const onAxisToggleChange = useCallback(
    (value: StyleValueAxis, field: ParentLayoutKeys) => {
      batch(() => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        submitStyle(field, value);
        submitStyle('child-sizes', getChildSizesArray(component, value));
      });
    },
    [submitStyle, component],
  );

  const onSubmitStyle = useCallback(
    (value: any, field: ParentLayoutKeys) => {
      if (field === 'gap') {
        submitStyle(field, {
          value: parseInt(value),
          unit: 'px',
        });
      } else {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        submitStyle(field, value);
      }
    },
    [submitStyle],
  );

  return (
    <SectionAccordion title='Layout'>
      <SectionRow>
        <SectionColumn>
          <ToggleStyleInput
            field='axis'
            editable={editable}
            name={`layout-${component.name}-axis-${styleVariantIndex}-toggle`}
            defaultValue={parentLayoutDefaults.axis.default}
            options={PARENT_LAYOUT_OPTIONS.axis}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onChange={onAxisToggleChange}
          />
        </SectionColumn>
      </SectionRow>
      <SectionRow>
        <SectionColumn>
          <ToggleStyleInput
            label={parentLayoutDefaults.alignment.label}
            field='alignment'
            editable={editable}
            name={`layout-${component.name}-alignment-${styleVariantIndex}-toggle`}
            defaultValue={parentLayoutDefaults.alignment.default}
            options={PARENT_LAYOUT_OPTIONS.alignment[parentLayoutDefaults.axis.default]}
            onChange={onSubmitStyle}
          />
        </SectionColumn>
        <SectionColumn>
          <StyleInput
            label={parentLayoutDefaults.gap.label}
            field='gap'
            name={`component-style-width-${component.name}-${styleVariantIndex}`}
            defaultValue={parentLayoutDefaults.gap.default.value.toString()}
            editable={editable}
            submit={onSubmitStyle}
          />
        </SectionColumn>
      </SectionRow>
      <SectionRow>
        <SectionColumn>
          <ToggleStyleInput
            label={parentLayoutDefaults['frame-positioning'].label}
            labelTooltip='Controls the width of a component with respect to the screen. Edge-to-edge sets component to span the entire screen, while inside margins sets the width with respect to the screen margins.'
            field='frame-positioning'
            editable={editable}
            name={`layout-${component.name}-frame-positioning-${styleVariantIndex}-toggle`}
            defaultValue={parentLayoutDefaults['frame-positioning'].default}
            options={PARENT_LAYOUT_OPTIONS['frame-positioning']}
            onChange={onSubmitStyle}
          />
        </SectionColumn>
      </SectionRow>
    </SectionAccordion>
  );
};

export default ParentLayoutStyles;
